import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, Bell, MessageSquare, RefreshCw, Calendar, Star, Megaphone, Loader2 } from 'lucide-react';
import dayjs from 'dayjs';
import { api } from '../apiClient';

const ICONS = {
  new_enquiry: MessageSquare,
  enquiry_status_changed: RefreshCw,
  event_reminder: Calendar,
  review_received: Star,
  organizer_status_changed: Megaphone,
  event_status_changed: Calendar,
};

export default function NotificationsPage() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const r = await api.get('/notifications');
      setItems(r.data);
    } catch {
      setItems([]);
    }
  }, []);

  useEffect(() => {
    setLoading(true);
    load().finally(() => setLoading(false));
  }, [load]);

  const markAllRead = async () => {
    await api.post('/notifications/read-all').catch(() => {});
    await load();
  };

  const openNotif = async (n) => {
    if (!n.read) {
      await api.post(`/notifications/${n.id}/read`).catch(() => {});
    }
    const eid = n.data?.event_id;
    if (n.type === 'organizer_status_changed') navigate('/profile');
    else if (n.type === 'event_status_changed' && eid) navigate(`/organizer/events/${eid}`);
    else if (n.type === 'new_enquiry' || n.type === 'review_received') navigate('/organizer/enquiries');
    else if (n.type === 'enquiry_status_changed') navigate('/enquiries');
    else if (eid) navigate(`/event/${eid}`);
    else await load();
  };

  return (
    <div className="max-w-3xl mx-auto px-6" data-testid="notifications-page">
      <div className="flex items-center justify-between py-4">
        <button data-testid="notif-back-btn" onClick={() => navigate(-1)} className="p-1 rounded-lg hover:bg-gray-100 transition-colors">
          <ChevronLeft className="w-6 h-6 text-gray-900" />
        </button>
        <h1 className="text-lg font-black text-gray-900">Notifications</h1>
        <button data-testid="mark-all-read-btn" onClick={markAllRead} className="text-brand-magenta text-xs font-extrabold hover:text-pink-700">
          Mark all
        </button>
      </div>

      {loading ? (
        <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-brand-magenta" /></div>
      ) : items.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20" data-testid="notif-empty">
          <Bell className="w-16 h-16 text-gray-300 mb-4" />
          <h3 className="text-lg font-extrabold text-gray-900">You're all caught up</h3>
          <p className="text-sm text-gray-500 mt-1 text-center">New enquiries and updates will appear here.</p>
        </div>
      ) : (
        <div className="flex flex-col gap-2 pb-8">
          {items.map((item) => {
            const Icon = ICONS[item.type] || Bell;
            return (
              <button
                key={item.id}
                data-testid={`notif-${item.id}`}
                onClick={() => openNotif(item)}
                className={`flex items-center gap-3 p-3 rounded-2xl border text-left transition-colors hover:bg-gray-50 ${
                  !item.read ? 'border-pink-200 bg-pink-50/30' : 'border-gray-100 bg-white'
                }`}
              >
                <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                  !item.read ? 'bg-brand-magenta' : 'bg-gray-100'
                }`}>
                  <Icon className={`w-4 h-4 ${!item.read ? 'text-white' : 'text-gray-500'}`} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-extrabold text-gray-900 line-clamp-2">{item.title}</p>
                  <p className="text-xs text-gray-500 mt-0.5 line-clamp-2">{item.body}</p>
                  <p className="text-[11px] text-gray-400 mt-1">{dayjs(item.created_at).format('MMM D · h:mm A')}</p>
                </div>
                {!item.read && <div className="w-2 h-2 rounded-full bg-brand-magenta flex-shrink-0" />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
